import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useTheme } from '../../context/ThemeContext';
import { 
  Layers, ChevronDown, ChevronRight, Search, TrendingUp, TrendingDown,
  Users, DollarSign, Building2, Filter, ArrowUpDown, UserCog, Check, Loader2
} from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import CohortRevenueAnalysis from './CohortRevenueAnalysis';
import { PERSONA_COLORS } from '../../utils/personaColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlanGroups = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [sortBy, setSortBy] = useState('revenue');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterMulti, setFilterMulti] = useState(false);
  const [managers, setManagers] = useState([]);
  const [reassigning, setReassigning] = useState(null); // planNumber being reassigned

  const fetchData = useCallback(async () => {
    try {
      const [groupRes, mgrRes] = await Promise.all([
        axios.get(`${API}/plan-groups`),
        axios.get(`${API}/managers`),
      ]);
      setData(groupRes.data);
      setManagers(mgrRes.data || []);
    } catch (err) {
      setError('Failed to load plan groups');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleReassign = async (planNumber, newManager) => {
    setReassigning(planNumber);
    try {
      await axios.put(`${API}/plan-config/${encodeURIComponent(planNumber)}`, { manager: newManager });
      await fetchData();
    } catch (err) {
      console.error('Failed to reassign:', err);
    } finally {
      setReassigning(null);
    }
  };

  // Filter and sort groups
  const filteredGroups = useMemo(() => {
    if (!data?.groups) return [];
    
    let groups = [...data.groups];
    
    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      groups = groups.filter(g =>
        g.basePlan.toLowerCase().includes(term) ||
        (g.primaryPropertyName || '').toLowerCase().includes(term) ||
        (g.propertyNames || []).some(n => (n || '').toLowerCase().includes(term)) ||
        g.managers.some(m => (m || '').toLowerCase().includes(term)) ||
        g.plans.some(p =>
          p.planNumber.toLowerCase().includes(term) ||
          (p.propertyName || '').toLowerCase().includes(term) ||
          (p.planName || '').toLowerCase().includes(term)
        )
      );
    }
    
    // Filter multi-plan groups only
    if (filterMulti) {
      groups = groups.filter(g => g.planCount > 1);
    }
    
    // Sort
    groups.sort((a, b) => {
      let valA, valB;
      switch (sortBy) {
        case 'plans':
          valA = a.planCount;
          valB = b.planCount;
          break;
        case 'lots':
          valA = a.totalOccupiableLots || 0;
          valB = b.totalOccupiableLots || 0;
          break;
        case 'margin':
          valA = a.margin;
          valB = b.margin;
          break;
        case 'workload':
          valA = a.totalWorkload;
          valB = b.totalWorkload;
          break;
        case 'profit':
          valA = a.profit;
          valB = b.profit;
          break;
        default:
          valA = a.totalRevenue;
          valB = b.totalRevenue;
      }
      return sortOrder === 'desc' ? valB - valA : valA - valB;
    });
    
    return groups;
  }, [data, searchTerm, sortBy, sortOrder, filterMulti]);

  const toggleGroup = (basePlan) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(basePlan)) {
        newSet.delete(basePlan);
      } else {
        newSet.add(basePlan);
      }
      return newSet;
    });
  };

  const toggleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`p-6 rounded-xl border ${isDark ? 'bg-red-500/10 border-red-500/20 text-red-400' : 'bg-red-50 border-red-200 text-red-600'}`}>
        {error || 'No data available'}
      </div>
    );
  }

  const { summary } = data;

  return (
    <div className="space-y-6" data-testid="plan-groups">
      {/* Header */}
      <div className={`relative overflow-hidden rounded-2xl p-8 border transition-colors duration-300
        ${isDark 
          ? 'bg-gradient-to-br from-blue-500/10 via-[#0d0d0f] to-emerald-500/10 border-white/5' 
          : 'bg-gradient-to-br from-blue-50 via-white to-emerald-50 border-slate-200'}`}>
        <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/10 rounded-full blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isDark ? 'bg-blue-500/20' : 'bg-blue-100'}`}>
              <Layers className="w-6 h-6 text-blue-500" />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>OC Plan Groups</h1>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Plans grouped by base number for consolidated analysis</p>
            </div>
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-6">
            <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Groups</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{summary.totalGroups}</p>
              <p className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                {summary.multiPlanGroups} multi-plan, {summary.singlePlanGroups} single
              </p>
            </div>
            <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Revenue</p>
              <p className="text-2xl font-bold text-emerald-500">${(summary.totalRevenue / 1000000).toFixed(2)}M</p>
            </div>
            <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Workload</p>
              <p className="text-2xl font-bold text-red-500">${(summary.totalWorkload / 1000000).toFixed(2)}M</p>
            </div>
            <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Profitable Groups</p>
              <p className={`text-2xl font-bold ${summary.profitableGroups > summary.unprofitableGroups ? 'text-emerald-500' : 'text-red-500'}`}>
                {summary.profitableGroups}/{summary.totalGroups}
              </p>
            </div>
            <div className={`p-4 rounded-xl ${isDark ? 'bg-white/5' : 'bg-white'}`}>
              <p className={`text-xs font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Total Lots</p>
              <p className={`text-2xl font-bold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>
                {summary.totalOccupiableLots ? summary.totalOccupiableLots.toLocaleString() : '-'}
              </p>
              {summary.revenuePerLot > 0 && (
                <p className={`text-xs ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>
                  ${summary.revenuePerLot.toLocaleString(undefined, {maximumFractionDigits: 0})}/lot
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Cohort Revenue Analysis - replaces RevenueCompositionChart */}
      {filteredGroups.length > 0 && (
        <CohortRevenueAnalysis groups={filteredGroups} />
      )}

      {/* Search and Filters */}
      <div className={`rounded-2xl border p-4 transition-colors duration-300
        ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}>
        <div className="flex flex-wrap gap-4 items-center">
          <div className="relative flex-1 min-w-[250px]">
            <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
            <input
              type="text"
              placeholder="Search by plan number, group, or manager..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 rounded-lg border text-sm transition-colors
                ${isDark 
                  ? 'bg-white/5 border-white/10 text-white placeholder-slate-500 focus:border-blue-500/50' 
                  : 'bg-slate-50 border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-500'
                } focus:outline-none focus:ring-1 focus:ring-blue-500/20`}
            />
          </div>
          
          <button
            onClick={() => setFilterMulti(!filterMulti)}
            className={`px-4 py-2 rounded-lg border text-sm font-medium transition-colors flex items-center gap-2
              ${filterMulti 
                ? 'bg-blue-500 border-blue-500 text-white' 
                : isDark 
                  ? 'bg-white/5 border-white/10 text-slate-300 hover:bg-white/10' 
                  : 'bg-slate-100 border-slate-200 text-slate-600 hover:bg-slate-200'}`}
          >
            <Filter className="w-4 h-4" />
            Multi-plan only ({summary.multiPlanGroups})
          </button>
          
          <div className="flex items-center gap-2">
            <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Sort by:</span>
            {[
              { id: 'revenue', label: 'Revenue' },
              { id: 'plans', label: 'Plans' },
              { id: 'lots', label: 'Lots' },
              { id: 'margin', label: 'Margin' },
              { id: 'profit', label: 'Gross Margin' }
            ].map(opt => (
              <button
                key={opt.id}
                onClick={() => toggleSort(opt.id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex items-center gap-1
                  ${sortBy === opt.id 
                    ? 'bg-blue-500/20 text-blue-500' 
                    : isDark 
                      ? 'bg-white/5 text-slate-400 hover:bg-white/10' 
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
              >
                {opt.label}
                {sortBy === opt.id && (
                  <ArrowUpDown className="w-3 h-3" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Groups List */}
      <div className="space-y-3">
        {filteredGroups.length === 0 ? (
          <div className={`p-12 rounded-xl border text-center transition-colors duration-300
            ${isDark ? 'bg-white/[0.02] border-white/5' : 'bg-slate-50 border-slate-200'}`}>
            <Layers className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-300'}`} />
            <p className={`text-lg font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              No groups found
            </p>
          </div>
        ) : (
          filteredGroups.map((group) => (
            <div 
              key={group.basePlan}
              className={`rounded-xl border overflow-hidden transition-colors duration-300
                ${isDark ? 'bg-[#0d0d0f]/50 border-white/5' : 'bg-white border-slate-200'}`}
            >
              {/* Group Header */}
              <button
                onClick={() => toggleGroup(group.basePlan)}
                className={`w-full p-4 flex items-center gap-4 transition-colors
                  ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-slate-50'}`}
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center
                  ${group.planCount > 1 
                    ? 'bg-blue-500/20 text-blue-500' 
                    : isDark ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-400'}`}>
                  {expandedGroups.has(group.basePlan) ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </div>
                
                <div className="flex-1 text-left">
                  <div className="flex items-center gap-3">
                    <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                      {group.basePlan}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                      ${group.planCount > 1 
                        ? 'bg-blue-500/20 text-blue-500' 
                        : isDark ? 'bg-white/5 text-slate-500' : 'bg-slate-100 text-slate-500'}`}>
                      {group.planCount} plan{group.planCount > 1 ? 's' : ''}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium
                      ${group.isProfitable ? 'bg-emerald-500/20 text-emerald-500' : 'bg-red-500/20 text-red-500'}`}>
                      {group.isProfitable ? 'Profitable' : 'Loss'}
                    </span>
                  </div>
                  <div className={`text-sm mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                    <span className="inline-flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {group.managers.slice(0, 2).join(', ')}
                      {group.managers.length > 2 && ` +${group.managers.length - 2}`}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-right">
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Lots (LUM)</p>
                    <p className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{group.totalOccupiableLots ? group.totalOccupiableLots.toLocaleString() : '—'}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Revenue</p>
                    <p className="font-bold text-emerald-500">${group.totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Workload</p>
                    <p className="font-bold text-red-500">${group.totalWorkload.toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                  </div>
                  <div className="text-right">
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Gross Margin</p>
                    <p className={`font-bold ${group.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                      {group.margin >= 0 ? '+' : ''}{group.margin.toFixed(1)}%
                    </p>
                  </div>
                  <div className="text-right w-24">
                    <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Persona</p>
                    <p className="font-medium text-xs" style={{ color: PERSONA_COLORS[group.dominantPersona] }}>
                      {group.dominantPersona}
                    </p>
                  </div>
                </div>
              </button>
              
              {/* Expanded Content */}
              {expandedGroups.has(group.basePlan) && (
                <div className={`border-t ${isDark ? 'border-white/5 bg-white/[0.01]' : 'border-slate-100 bg-slate-50/50'}`}>
                  {/* Group Details */}
                  <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Cost Breakdown */}
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-white/[0.02]' : 'bg-white'}`}>
                      <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Cost Breakdown
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Email Cost</span>
                          <span className={isDark ? 'text-white' : 'text-slate-900'}>${group.totalMessageCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Handle Cost</span>
                          <span className={isDark ? 'text-white' : 'text-slate-900'}>${group.totalHandleCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Meeting Cost</span>
                          <span className={isDark ? 'text-white' : 'text-slate-900'}>${group.totalMeetingCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className={`flex justify-between pt-2 border-t font-bold ${isDark ? 'border-white/5' : 'border-slate-200'}`}>
                          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Total Workload</span>
                          <span className="text-red-500">${group.totalWorkload.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className="flex justify-between font-bold">
                          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Gross Profit</span>
                          <span className={group.profit >= 0 ? 'text-emerald-500' : 'text-red-500'}>
                            {group.profit >= 0 ? '+' : ''}${group.profit.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Metrics */}
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-white/[0.02]' : 'bg-white'}`}>
                      <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Metrics
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Total Meetings</span>
                          <span className={isDark ? 'text-white' : 'text-slate-900'}>{group.totalMeetings}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Avg Revenue/Plan</span>
                          <span className="text-emerald-500">${group.avgRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Avg Workload/Plan</span>
                          <span className="text-red-500">${group.avgWorkload.toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                        </div>
                        {group.avgCompliance !== null && (
                          <div className="flex justify-between">
                            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Avg Compliance</span>
                            <span className={isDark ? 'text-white' : 'text-slate-900'}>{group.avgCompliance.toFixed(0)}%</span>
                          </div>
                        )}
                        {group.avgNPS !== null && (
                          <div className="flex justify-between">
                            <span className={isDark ? 'text-slate-400' : 'text-slate-500'}>Avg NPS</span>
                            <span className={isDark ? 'text-white' : 'text-slate-900'}>{group.avgNPS.toFixed(1)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Persona Distribution */}
                    <div className={`p-4 rounded-lg ${isDark ? 'bg-white/[0.02]' : 'bg-white'}`}>
                      <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        Persona Distribution
                      </h4>
                      <div className="h-32">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(group.personas).map(([name, value]) => ({ name, value }))}
                              cx="50%"
                              cy="50%"
                              innerRadius={25}
                              outerRadius={45}
                              paddingAngle={2}
                              dataKey="value"
                            >
                              {Object.entries(group.personas).map(([name]) => (
                                <Cell key={name} fill={PERSONA_COLORS[name]} />
                              ))}
                            </Pie>
                            <Tooltip 
                              contentStyle={{ 
                                backgroundColor: isDark ? '#1a1a1f' : '#fff',
                                border: isDark ? '1px solid rgba(255,255,255,0.1)' : '1px solid #e2e8f0',
                                borderRadius: '8px'
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {Object.entries(group.personas).map(([name, count]) => (
                          <span key={name} className="text-xs" style={{ color: PERSONA_COLORS[name] }}>
                            {name}: {count}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Plans Table */}
                  <div className="p-4">
                    <h4 className={`text-xs font-bold uppercase tracking-wider mb-3 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      Plans in Group
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className={isDark ? 'text-slate-500' : 'text-slate-400'}>
                            <th className="text-left py-2 font-medium">Plan Number</th>
                            <th className="text-left py-2 font-medium">Manager</th>
                            <th className="text-right py-2 font-medium">Revenue</th>
                            <th className="text-right py-2 font-medium">Workload</th>
                            <th className="text-right py-2 font-medium">Margin</th>
                            <th className="text-left py-2 font-medium">Persona</th>
                          </tr>
                        </thead>
                        <tbody>
                          {group.plans.map((plan) => (
                            <tr key={plan.planNumber} className={`border-t ${isDark ? 'border-white/5' : 'border-slate-100'}`}>
                              <td className={`py-2 font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                {plan.planNumber}
                              </td>
                              <td className="py-2">
                                <div className="flex items-center gap-1.5">
                                  {reassigning === plan.planNumber ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#A3A3A3]" />
                                  ) : (
                                    <select
                                      data-testid={`reassign-${plan.planNumber}`}
                                      value={plan.manager}
                                      onChange={(e) => handleReassign(plan.planNumber, e.target.value)}
                                      className={`text-sm py-1 px-2 rounded-md border cursor-pointer transition-colors
                                        ${isDark
                                          ? 'bg-white/5 border-white/10 text-slate-300 hover:border-white/20'
                                          : 'bg-white border-[#E5E5E5] text-[#171717] hover:border-[#A3A3A3]'
                                        } focus:outline-none focus:ring-1 focus:ring-[#171717]/10`}
                                    >
                                      {managers.includes(plan.manager) ? null : (
                                        <option value={plan.manager}>{plan.manager}</option>
                                      )}
                                      {managers.map(m => (
                                        <option key={m} value={m}>{m}</option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                              </td>
                              <td className="py-2 text-right text-emerald-500">
                                ${plan.revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </td>
                              <td className="py-2 text-right text-red-500">
                                ${plan.workloadCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                              </td>
                              <td className={`py-2 text-right ${plan.margin >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                {(plan.margin * 100).toFixed(1)}%
                              </td>
                              <td className="py-2" style={{ color: PERSONA_COLORS[plan.persona] }}>
                                {plan.persona}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
      
      {/* Results count */}
      <div className={`text-center text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
        Showing {filteredGroups.length} of {data.groups.length} groups
      </div>
    </div>
  );
};

export default PlanGroups;
